import type { CreditTransactionViewModel } from '@/components/settings/credits/credit-transaction-view-model';
import {
  getUserFacingReadRetryDelay,
  retryUserFacingReadQuery,
} from '@/lib/query/retry-policy';
import {
  keepPreviousData,
  queryOptions,
  useQuery,
} from '@tanstack/react-query';
import type { SortingState } from '@tanstack/react-table';
import { useLocale } from 'next-intl';
import {
  type CreditBalanceResult,
  fetchCreditBalanceFromApi,
} from './fetch-credit-balance';

type CreditsApiResponse<T> = {
  success: boolean;
  data?: T;
  error?: string;
};

async function readCreditsApiJson<T>(
  response: Response,
  fallbackError: string
): Promise<T> {
  const result = (await response.json()) as CreditsApiResponse<T>;
  if (!response.ok || !result.success) {
    throw new Error(result.error || fallbackError);
  }

  if (typeof result.data === 'undefined') {
    throw new Error(`${fallbackError}: missing data`);
  }

  return result.data;
}

async function fetchCreditStatsFromApi() {
  const response = await fetch('/api/credits/stats', {
    method: 'GET',
    credentials: 'include',
  });

  return readCreditsApiJson<{
    expiringCredits: {
      amount: number;
      withinDays: number;
    };
  }>(response, 'Failed to fetch credit stats');
}

async function fetchCreditTransactionsFromApi(input: {
  pageIndex: number;
  pageSize: number;
  search: string;
  sorting: SortingState;
  locale: string;
}) {
  const response = await fetch('/api/credits/transactions', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(input),
  });

  return readCreditsApiJson<{
    items: CreditTransactionViewModel[];
    total: number;
  }>(response, 'Failed to fetch credit transactions');
}

// Query keys
export const creditsKeys = {
  all: ['credits'] as const,
  balance: () => [...creditsKeys.all, 'balance'] as const,
  stats: () => [...creditsKeys.all, 'stats'] as const,
  transactions: () => [...creditsKeys.all, 'transactions'] as const,
  transactionsList: (filters: {
    pageIndex: number;
    pageSize: number;
    search: string;
    sorting: SortingState;
    locale: string;
  }) => [...creditsKeys.transactions(), filters] as const,
};

export async function fetchCreditBalance() {
  return fetchCreditBalanceFromApi();
}

export function getCreditBalanceQueryOptions() {
  return queryOptions({
    queryKey: creditsKeys.balance(),
    queryFn: fetchCreditBalance,
    staleTime: Number.POSITIVE_INFINITY,
    retry: retryUserFacingReadQuery,
    retryDelay: getUserFacingReadRetryDelay,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
  });
}

// Hook to fetch credit balance
export function useCreditBalance(options?: { enabled?: boolean }) {
  return useQuery({
    ...getCreditBalanceQueryOptions(),
    enabled: options?.enabled ?? true,
    // 只在显式触发时刷新
  });
}

// Hook to fetch credit statistics
export function useCreditStats() {
  return useQuery({
    queryKey: creditsKeys.stats(),
    queryFn: fetchCreditStatsFromApi,
    retry: retryUserFacingReadQuery,
    retryDelay: getUserFacingReadRetryDelay,
    // 与余额保持一致：只在显式触发时刷新
  });
}

// Hook to fetch credit transactions with pagination, search, and sorting
export function useCreditTransactions(
  pageIndex: number,
  pageSize: number,
  search: string,
  sorting: SortingState
) {
  const locale = useLocale();

  return useQuery({
    queryKey: creditsKeys.transactionsList({
      pageIndex,
      pageSize,
      search,
      sorting,
      locale,
    }),
    queryFn: async () => {
      const result = await fetchCreditTransactionsFromApi({
        pageIndex,
        pageSize,
        search,
        sorting,
        locale,
      });

      return {
        items: result.items || [],
        total: result.total || 0,
      };
    },
    placeholderData: keepPreviousData,
    retry: retryUserFacingReadQuery,
    retryDelay: getUserFacingReadRetryDelay,
  });
}
